import { Badge, Box, Button, ButtonGroup, SimpleGrid, Text, useDisclosure, VStack } from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { getPendingWithdrawalsForApproval } from 'services/api.service';
import { maskCurrency } from 'utils/currency';
import { Withdrawal } from '.';
import ApproveWithdrawal from './components/ApproveWithdrawal';
import Table from 'components/Table';

export enum StatusWithdrawal {
  PENDING = 'Pendente',
  PENDING_APPROVAL = 'Aguardando Aprovação',
  IN_PAYMENT = 'Em Pagamento',
  FINISHED = 'Finalizado',
  CANCELED = 'Cancelado',
}

export default function WithdrawalApprovals() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);

  const { data: withdrawals = [], isLoading } = useQuery({
    queryKey: ['pendingWithdrawalsForApproval'],
    queryFn: getPendingWithdrawalsForApproval,
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });

  const handleApprove = useCallback((withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    onOpen();
  }, [onOpen]);

  const handleCloseModal = () => {
    setSelectedWithdrawal(null);
    onClose();
  };

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Data',
          accessor: 'createdAt',
          Cell: ({ value }) => {
            const date = new Date(value);
            return (
              <VStack
                align="start"
                spacing={0}
              >
                <Text fontSize="sm">{date.toLocaleDateString('pt-BR')}</Text>
                <Text
                  fontSize="xs"
                  color="gray.500"
                >
                  {date.toLocaleTimeString('pt-BR')}
                </Text>
              </VStack>
            );
          },
        },
        {
          Header: 'Cliente',
          accessor: 'Customer',
          Cell: ({ value }) => (
            <VStack
              align="start"
              spacing={0}
            >
              <Text fontWeight="bold">{value.name}</Text>
              <Text
                fontSize="xs"
                color="gray.500"
              >
                Conta: {value.accountNumber}
              </Text>
            </VStack>
          ),
        },
        {
          Header: 'Valor',
          accessor: 'amount',
          Cell: ({ value, row }) => (
            <VStack
              align="start"
              spacing={0}
            >
              <Text
                fontWeight="bold"
                color="brand.500"
              >
                {maskCurrency(Number(value))}
              </Text>
              <Text
                fontSize="xs"
                color="gray.500"
              >
                Taxa: {maskCurrency(Number(row.original.fee))}
              </Text>
            </VStack>
          ),
        },
        {
          Header: 'Tipo',
          accessor: 'type',
          Cell: ({ value }) => {
            const types: Record<string, string> = {
              DICT: 'PIX',
              QRCODE: 'QR Code',
              BILLET: 'Boleto',
              MANU: 'Manual',
            };
            return <Badge colorScheme="blue">{types[value] || value}</Badge>;
          },
        },
        {
          Header: 'Destino',
          accessor: 'CustomerAccounts',
          Cell: ({ value }) => {
            if (!value) return <Text fontSize="sm">-</Text>;
            return (
              <VStack
                align="start"
                spacing={0}
              >
                <Text fontSize="sm">{value.accountHolder || '-'}</Text>
                <Text
                  fontSize="xs"
                  color="gray.500"
                >
                  {value.pix || `${value.bankIspb || ''} - Ag: ${value.agency || ''} / Conta: ${value.account || ''}`}
                </Text>
              </VStack>
            );
          },
        },
        {
          Header: 'Descrição',
          accessor: 'description',
          Cell: ({ value }) => (
            <Text
              fontSize="sm"
              noOfLines={2}
            >
              {value || '-'}
            </Text>
          ),
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ value }) => {
            const statusConfig: Record<
              string,
              {
                label: string;
                colorScheme: string;
              }
            > = {
              PENDING: { label: 'Pendente', colorScheme: 'yellow' },
              PENDING_APPROVAL: { label: 'Aguardando Aprovação', colorScheme: 'orange' },
              IN_PAYMENT: { label: 'Em Pagamento', colorScheme: 'blue' },
              FINISHED: { label: 'Finalizado', colorScheme: 'green' },
              CANCELED: { label: 'Cancelado', colorScheme: 'red' },
            };

            const config = statusConfig[value] || { label: value, colorScheme: 'gray' };

            return <Badge colorScheme={config.colorScheme}>{config.label}</Badge>;
          },
        },
        {
          Header: 'Aprovações',
          accessor: 'WithdrawalApprovals',
          Cell: ({ value, row }: { value: any; row: any }) => {
            const approvals = value || [];
            const approved = approvals.filter((a: { status: string }) => a.status === 'APPROVED').length;
            const required = row.original.Customer?.withdrawalApprovalsRequired || 0;

            return (
              <VStack
                align="start"
                spacing={0}
              >
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                >
                  {approved} / {required}
                </Text>
                {approvals.length > 0 && (
                  <Text
                    fontSize="xs"
                    color="gray.500"
                  >
                    {approvals.map((a: { approver?: { name?: string } }) => a.approver?.name).join(', ')}
                  </Text>
                )}
              </VStack>
            );
          },
        },
        {
          Header: 'Ações',
          Cell: ({ row }) => (
            <ButtonGroup
              size="sm"
              spacing={2}
            >
              <Button
                colorScheme="brand"
                onClick={() => handleApprove(row.original)}
              >
                Revisar
              </Button>
            </ButtonGroup>
          ),
        },
      ] as Column<Withdrawal>[],
    [handleApprove],
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <SimpleGrid
        columns={{ base: 1 }}
        gap="20px"
        mb="20px"
      >
        <Card>
          <Box mb={4}>
            <Text
              fontSize="2xl"
              fontWeight="bold"
              mb={2}
            >
              Aprovação de Saques
            </Text>
            <Text color="gray.600">
              Saques aguardando sua aprovação. Revise os detalhes e aprove ou rejeite cada solicitação.
            </Text>
          </Box>

          {withdrawals.length === 0 && !isLoading ? (
            <Box
              textAlign="center"
              py={10}
            >
              <Text
                fontSize="lg"
                color="gray.500"
              >
                Não há saques pendentes de aprovação
              </Text>
            </Box>
          ) : (
            <Table
              columnsData={columns}
              itensData={withdrawals}
              actions={undefined}
              setFilter={() => {}}
            />
          )}
        </Card>
      </SimpleGrid>

      {selectedWithdrawal && (
        <ApproveWithdrawal
          isOpen={isOpen}
          onClose={handleCloseModal}
          withdrawal={selectedWithdrawal}
        />
      )}
    </Box>
  );
}
